import { LanguageType } from 'types';

import { DateTime } from 'luxon';
import { z } from 'zod/v4';

export const DICTIONARY = {
	[LanguageType.English]: {
		validation: {
			firstName: 'First name can only contain letters, spaces and hyphens',
			lastName: 'Last name can only contain letters and hyphens',
			dateOfBirth: 'Please enter a valid date',
			phoneNumber: 'Please enter a valid phone number with country code (e.g., +140050600)',
			email: 'Please enter a valid email address'
		}
	},

	[LanguageType.Norwegian]: {
		validation: {
			firstName: 'Fornavn kan bare inneholde bokstaver, mellomrom og bindestrek',
			lastName: 'Etternavn kan bare inneholde bokstaver og bindestrek',
			dateOfBirth: 'Angi en gyldig dato',
			phoneNumber: 'Angi et gyldig telefonnummer med landskode (f.eks. +4740050600)',
			email: 'Angi en gyldig e-postadresse'
		}
	}
} as const;

export const createCodelistRecordSchema = (language: LanguageType) => {
	const translation = DICTIONARY[language];

	return z.object({
		customId: z
			.string()
			.transform(val => (val === '' ? undefined : val))
			.optional(),

		ssn: z
			.string()
			.transform(val => (val === '' ? undefined : val))
			.optional(),
		firstName: z
			.string()
			.transform(val => (val === '' ? undefined : val))
			.optional()
			.refine(
				val => val === undefined || /^[\p{L}\s-]+$/u.test(val),
				translation.validation.firstName
			),
		lastName: z
			.string()
			.transform(val => (val === '' ? undefined : val))
			.optional()
			.refine(
				val => val === undefined || /^[\p{L}\s-]+$/u.test(val),
				translation.validation.lastName
			),
		dateOfBirth: z
			.string()
			.transform(val => (val?.trim() === '' ? undefined : val))
			.optional()
			.refine(val => {
				if (val === undefined) return true;
				// Check if it's a valid ISO date string
				const date = new Date(val);
				return !isNaN(date.getTime());
			}, translation.validation.dateOfBirth)
			.transform(val =>
				val ? DateTime.fromJSDate(new Date(val)).toFormat('yyyy-MM-dd') : undefined
			),
		mobilePhone: z
			.string()
			.transform(val => (val === '' ? undefined : val))
			.optional()
			.refine(val => {
				if (val === undefined) return true;
				// E.164 format validation
				return z.e164().safeParse(val).success;
			}, translation.validation.phoneNumber),
		email: z
			.string()
			.transform(val => (val === '' ? undefined : val))
			.optional()
			.refine(val => {
				if (val === undefined) return true;
				// Email validation
				return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
			}, translation.validation.email),
		address: z
			.string()
			.transform(val => (val === '' ? undefined : val))
			.optional(),
		postalCode: z
			.string()
			.transform(val => (val === '' ? undefined : val))
			.optional(),
		postalPlace: z
			.string()
			.transform(val => (val === '' ? undefined : val))
			.optional(),
		country: z
			.string()
			.transform(val => (val === '' ? undefined : val))
			.optional()
	});
};
export type CodelistRecord = z.infer<ReturnType<typeof createCodelistRecordSchema>>;
export type CodelistRecordKey = keyof CodelistRecord;
export const codelistRecordKeys = Object.keys(
	createCodelistRecordSchema(LanguageType.English).shape
) as CodelistRecordKey[];
